<template>
  <div class="json-property">
    <!-- Object -->
    <div
      v-if="isObject"
      class="json-object"
      :class="{ 'has-unsaved-changes': hasUnsavedChanges }"
    >
      <div class="json-line">
        <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
        <span v-if="propertyKey" class="json-colon">:</span>
        <span class="json-bracket">{</span>
        <v-btn
          v-if="Object.keys(data).length > 0"
          icon
          size="x-small"
          variant="text"
          class="json-toggle"
          @click="toggleExpanded"
        >
          <v-icon>{{
            isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right'
          }}</v-icon>
        </v-btn>
      </div>

      <div v-if="isExpanded" class="json-children">
        <div v-for="childKey in sortedKeys" :key="childKey" class="json-child">
          <JsonProperty
            :data="data[childKey]"
            :property-key="childKey"
            :key-path="keyPath ? `${keyPath}.${childKey}` : childKey"
            :depth="depth + 1"
            :has-changes="hasChanges"
            :original-value="getChildOriginalValue(childKey)"
            @update="
              (keyPath, newValue) => {
                console.log(
                  'Object child update - parent keyPath:',
                  props.keyPath,
                  'childKey:',
                  childKey,
                  'received keyPath:',
                  keyPath
                )
                $emit('update', keyPath, newValue)
              }
            "
          />
        </div>
      </div>

      <div class="json-line">
        <span class="json-bracket">}</span>
      </div>
    </div>

    <!-- Array -->
    <div
      v-else-if="isArray"
      class="json-array"
      :class="{ 'has-unsaved-changes': hasUnsavedChanges }"
    >
      <div class="json-line">
        <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
        <span v-if="propertyKey" class="json-colon">:</span>
        <span class="json-bracket">[</span>
        <v-btn
          v-if="data.length > 0"
          icon
          size="x-small"
          variant="text"
          class="json-toggle"
          @click="toggleExpanded"
        >
          <v-icon>{{
            isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right'
          }}</v-icon>
        </v-btn>
      </div>

      <div v-if="isExpanded" class="json-children">
        <div v-for="(item, index) in data" :key="index" class="json-child">
          <JsonProperty
            :data="item"
            :property-key="String(index)"
            :key-path="keyPath ? `${keyPath}[${index}]` : String(index)"
            :depth="depth + 1"
            :has-changes="hasChanges"
            :original-value="getChildOriginalValue(index)"
            @update="
              (keyPath, newValue) => {
                console.log(
                  'Array child update - parent keyPath:',
                  props.keyPath,
                  'index:',
                  index,
                  'received keyPath:',
                  keyPath
                )
                $emit('update', keyPath, newValue)
              }
            "
          />
        </div>
      </div>

      <div class="json-line">
        <span class="json-bracket">]</span>
      </div>
    </div>

    <!-- String (editable) -->
    <div
      v-else-if="isString"
      class="json-string"
      :class="{ 'has-unsaved-changes': hasUnsavedChanges }"
    >
      <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
      <span v-if="propertyKey" class="json-colon">:</span>
      <div class="json-value-wrapper">
        <input
          v-if="isEditing"
          ref="inputRef"
          v-model="editingValue"
          class="json-value-input"
          @blur="finishEdit"
          @propertyKeydown.enter="finishEdit"
          @propertyKeydown.escape="cancelEdit"
        />
        <span v-else class="json-value-editable" @click="startEdit">
          "{{ data }}"
        </span>
      </div>
    </div>

    <!-- Number -->
    <div
      v-else-if="isNumber"
      class="json-number"
      :class="{ 'has-unsaved-changes': hasUnsavedChanges }"
    >
      <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
      <span v-if="propertyKey" class="json-colon">:</span>
      <div class="json-value-wrapper">
        <input
          v-if="isEditing"
          ref="inputRef"
          v-model="editingValue"
          type="number"
          class="json-value-input"
          @blur="finishEdit"
          @propertyKeydown.enter="finishEdit"
          @propertyKeydown.escape="cancelEdit"
        />
        <span v-else class="json-value-editable" @click="startEdit">
          {{ data }}
        </span>
      </div>
    </div>

    <!-- Boolean -->
    <div
      v-else-if="isBoolean"
      class="json-boolean"
      :class="{ 'has-unsaved-changes': hasUnsavedChanges }"
    >
      <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
      <span v-if="propertyKey" class="json-colon">:</span>
      <div class="json-value-wrapper">
        <input
          v-if="isEditing"
          ref="inputRef"
          v-model="editingValue"
          type="checkbox"
          class="json-value-input"
          @blur="finishEdit"
          @propertyKeydown.enter="finishEdit"
          @propertyKeydown.escape="cancelEdit"
        />
        <span v-else class="json-value-editable" @click="startEdit">
          {{ data }}
        </span>
      </div>
    </div>

    <!-- Null -->
    <div v-else class="json-null">
      <span v-if="propertyKey" class="json-key">"{{ propertyKey }}"</span>
      <span v-if="propertyKey" class="json-colon">:</span>
      <span class="json-value">null</span>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick } from 'vue'

  const props = defineProps({
    data: {
      required: true,
    },
    propertyKey: {
      type: String,
      default: '',
    },
    keyPath: {
      type: String,
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    originalValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
  })

  const emit = defineEmits(['update'])

  // Sort object keys for consistent display order
  const sortedKeys = computed(() => {
    if (!isObject.value) return []
    return Object.keys(props.data).sort()
  })

  const isExpanded = ref(true)
  const isEditing = ref(false)
  const editingValue = ref('')
  const inputRef = ref(null)

  const isObject = computed(() => {
    return (
      props.data && typeof props.data === 'object' && !Array.isArray(props.data)
    )
  })

  const isArray = computed(() => {
    return Array.isArray(props.data)
  })

  const isString = computed(() => {
    return typeof props.data === 'string'
  })

  const isNumber = computed(() => {
    return typeof props.data === 'number'
  })

  const isBoolean = computed(() => {
    return typeof props.data === 'boolean'
  })

  const hasUnsavedChanges = computed(() => {
    if (!props.hasChanges || !props.originalValue) return false

    // Only highlight primitive values (actual input fields), not objects/arrays
    if (typeof props.data !== 'object' || props.data === null) {
      return JSON.stringify(props.data) !== JSON.stringify(props.originalValue)
    }

    // Don't highlight parent containers
    return false
  })

  // Helper function to get original value for child properties
  const getChildOriginalValue = (childKey) => {
    if (!props.originalValue || typeof props.originalValue !== 'object')
      return null
    return props.originalValue[childKey] || null
  }

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value
  }

  const startEdit = async () => {
    isEditing.value = true
    editingValue.value = props.data
    await nextTick()
    if (inputRef.value) {
      inputRef.value.focus()
      inputRef.value.select()
    }
  }

  const finishEdit = () => {
    if (isEditing.value) {
      let newValue = editingValue.value

      // Convert based on original type
      if (isNumber.value) {
        newValue = parseFloat(newValue) || 0
      } else if (isBoolean.value) {
        newValue = editingValue.value === 'true' || editingValue.value === true
      }

      // For leaf values being edited, use the keyPath as-is
      // The keyPath already contains the full path to this property
      let fullPath = props.keyPath

      console.log(
        'JsonProperty finishEdit - props.keyPath:',
        props.keyPath,
        'props.propertyKey:',
        props.propertyKey,
        'fullPath:',
        fullPath,
        'newValue:',
        newValue
      )
      emit('update', fullPath, newValue)
      isEditing.value = false
    }
  }

  const cancelEdit = () => {
    isEditing.value = false
    editingValue.value = props.data
  }
</script>

<style scoped>
  .json-property {
    position: relative;
  }

  .json-line {
    display: flex;
    align-items: center;
    margin: 1px 0;
  }

  .json-children {
    position: relative;
    margin-left: 16px;
  }

  .json-children::before {
    content: '';
    position: absolute;
    left: -4px;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: #3c3c3c;
  }

  .json-child {
    position: relative;
  }

  .json-child::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 4px;
    height: 1px;
    background-color: #3c3c3c;
  }

  .json-key {
    color: #ff5c57;
    font-weight: 500;
  }

  .json-colon {
    color: #d4d4d4;
    margin: 0 8px;
  }

  .json-bracket {
    color: #d4d4d4;
    font-weight: bold;
  }

  .json-value {
    color: #c3d82c;
  }

  .json-value-editable {
    color: #c3d82c;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 2px;
    transition: background-color 0.2s;
  }

  .json-value-editable:hover {
    background-color: #2d2d30;
  }

  .json-value-wrapper {
    display: inline-block;
    flex: 1;
  }

  .json-value-input {
    background-color: #2d2d30;
    border: 1px solid #3c3c3c;
    color: #c3d82c;
    padding: 2px 4px;
    border-radius: 2px;
    font-family: inherit;
    font-size: inherit;
    width: 100%;
    min-width: 200px;
    outline: none;
  }

  .json-value-input:focus {
    border-color: #c3d82c;
    box-shadow: 0 0 0 1px #c3d82c;
  }

  .json-toggle {
    margin-left: 8px;
    color: #d4d4d4;
    min-width: 24px;
    height: 24px;
  }

  .json-string .json-value {
    color: #c3d82c;
  }

  .json-number .json-value {
    color: #b5cea8;
  }

  .json-boolean .json-value {
    color: #569cd6;
  }

  .json-null .json-value {
    color: #808080;
    font-style: italic;
  }

  /* Highlighting for unsaved changes */
  .json-object.has-unsaved-changes,
  .json-array.has-unsaved-changes,
  .json-string.has-unsaved-changes,
  .json-number.has-unsaved-changes,
  .json-boolean.has-unsaved-changes {
    background-color: #fff3cd !important;
    border-left: 3px solid #ffc107;
    padding-left: 8px;
    margin-left: -8px;
    border-radius: 4px;
  }

  .json-object.has-unsaved-changes .json-key,
  .json-array.has-unsaved-changes .json-key,
  .json-string.has-unsaved-changes .json-key,
  .json-number.has-unsaved-changes .json-key,
  .json-boolean.has-unsaved-changes .json-key {
    color: #856404 !important;
    font-weight: 600;
  }

  .json-object.has-unsaved-changes .json-value,
  .json-array.has-unsaved-changes .json-value,
  .json-string.has-unsaved-changes .json-value,
  .json-number.has-unsaved-changes .json-value,
  .json-boolean.has-unsaved-changes .json-value {
    color: #856404 !important;
    font-weight: 600;
  }

  .json-object.has-unsaved-changes,
  .json-array.has-unsaved-changes,
  .json-string.has-unsaved-changes,
  .json-number.has-unsaved-changes,
  .json-boolean.has-unsaved-changes {
    background-color: #fff3cd !important;
    border-left: 3px solid #ffc107;
    padding-left: 8px;
    margin-left: -8px;
    border-radius: 4px;
  }

  .json-object.has-unsaved-changes .json-key,
  .json-array.has-unsaved-changes .json-key,
  .json-string.has-unsaved-changes .json-key,
  .json-number.has-unsaved-changes .json-key,
  .json-boolean.has-unsaved-changes .json-key {
    color: #856404 !important;
    font-weight: 600;
  }

  .json-object.has-unsaved-changes .json-value,
  .json-array.has-unsaved-changes .json-value,
  .json-string.has-unsaved-changes .json-value,
  .json-number.has-unsaved-changes .json-value,
  .json-boolean.has-unsaved-changes .json-value {
    color: #856404 !important;
    font-weight: 600;
  }

  .json-string.has-unsaved-changes .json-value-input,
  .json-number.has-unsaved-changes .json-value-input,
  .json-boolean.has-unsaved-changes .json-value-input {
    background-color: #fff3cd !important;
    color: #856404 !important;
    font-weight: 600;
    border-color: #ffc107 !important;
  }

  .json-string.has-unsaved-changes .json-value-editable,
  .json-number.has-unsaved-changes .json-value-editable,
  .json-boolean.has-unsaved-changes .json-value-editable {
    background-color: #fff3cd !important;
    color: #856404 !important;
    font-weight: 600;
  }
</style>
